// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A control component for managing asset functional status.
 * This module provides a dropdown interface for changing
 * an asset's functional status with visual indicators
 * and real-time updates. It supports all defined status
 * types and handles state changes efficiently.
 *
 * Key features:
 * - Status selection
 * - Visual indicators
 * - Real-time updates
 * - Error handling
 * - Dropdown menu
 */
import { Asset } from '@/asset/api/types';
import { UpdateAssetParameters, updateAsset } from '@/asset/api/update-asset';
import { AssetFunctionalStatusIndicator } from '@/asset/components/functional-status-indicator';
import {
  ASSET_FUNCTIONAL_STATUSES,
  AssetFunctionalStatus,
} from '@/constants/asset-functional-status';
import { ExpandMoreRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Button,
  Divider,
  List,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

/**
 * Props for configuring the AssetFunctionalStatusControl component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to modify.
   * Must include functional status and key fields.
   */
  asset: Asset;
}

/**
 * A control component for changing asset functional status.
 * Provides a dropdown menu with visual indicators for each
 * available status and handles updates with error handling.
 *
 * Features:
 * - Status selection dropdown
 * - Visual status indicators
 * - Real-time status updates
 * - Error handling
 * - Query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ asset }: { asset: Asset }) {
 *   return (
 *     <AssetFunctionalStatusControl
 *       asset={asset}
 *     />
 *   );
 * }
 * ```
 */
export function AssetFunctionalStatusControl(props: Props) {
  const menu = useDisclosure();
  const queryClient = useQueryClient();
  const buttonElement = useRef<HTMLButtonElement>(null);

  const updateAssetMutation = useMutation({
    mutationFn: (data: UpdateAssetParameters) =>
      updateAsset(props.asset.key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      menu.close();
    },
  });

  /**
   * Creates a click handler for a specific status.
   * Returns a function that updates the asset status
   * when the menu item is clicked.
   *
   * @param status - The status to set when clicked
   * @returns Click handler function
   */
  const createClickHandler = (status: AssetFunctionalStatus) => () => {
    updateAssetMutation.mutate({ functionalStatus: status });
  };

  return (
    <>
      <Button
        ref={buttonElement}
        color="inherit"
        onClick={menu.open}
        sx={{ paddingY: 0.5, paddingX: 0.5 }}
      >
        <AssetFunctionalStatusIndicator
          variant="chip"
          status={props.asset.functionalStatus}
          tooltip={false}
        />
        <Typography
          variant="body2"
          display="flex"
          alignItems="center"
          marginLeft={1}
        >
          <ExpandMoreRounded fontSize="inherit" color="action" />
        </Typography>
      </Button>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={buttonElement.current}
        open={menu.isOpen}
        onClose={menu.close}
        sx={{ marginTop: 1 }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          paddingX={1}
          paddingBottom={1}
          minWidth={200}
        >
          Set status
        </Typography>
        <Divider />
        <List disablePadding>
          {ASSET_FUNCTIONAL_STATUSES.map((status) => (
            <MenuItem
              disableGutters
              key={status}
              onClick={createClickHandler(status)}
              sx={{ padding: 1 }}
            >
              <AssetFunctionalStatusIndicator
                variant="chip"
                status={status}
                tooltip={false}
              />
            </MenuItem>
          ))}
        </List>
      </Menu>
    </>
  );
}
